import React, { useState, useEffect } from 'react';
import { useHRMS } from '../context/HRMSContext.jsx';
import api from '../services/api.js';
import Message from '../components/Message.jsx';
import PermissionGuard from "../components/PermissionGuard.jsx";
import { MODULES, ACTIONS } from "../context/PermissionsContext.jsx";
import CommonTable from "../components/common/CommonTable.jsx";
function LeaveTypeMaster() {
  const { state, loadLeaveTypes } = useHRMS();
  const [showModal, setShowModal] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [formData, setFormData] = useState({
    leaveName: '',
    paidUnpaid: 'Paid',
    maxPerYear: '',
    byExperience: false,
    experienceRanges: [{
      experienceFrom: '',
      experienceTo: '',
      experienceMaxPerYear: ''
    }]
  });
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [actionLoadingText, setActionLoadingText] = useState("Processing...");
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  const [leaveTypeFilters, setLeaveTypeFilters] = useState({
    leaveName: "",
    type: "",
  });

  useEffect(() => {
    loadLeaveTypes();
  }, []);

  //console.log('LeaveTypes state:', state.leaveTypes);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Check for duplicate leave type name
    const existingLeaveType = (state.leaveTypes || []).find(leaveType =>
      leaveType.leaveName.toLowerCase() === formData.leaveName.toLowerCase() &&
      (!editingLeaveType || leaveType.id !== editingLeaveType.id)
    );

    if (existingLeaveType) {
      setMessage({ text: 'Leave type name already exists!', type: 'error' });
      return;
    }

    // Validate experience ranges
    if (formData.byExperience) {
      for (let i = 0; i < formData.experienceRanges.length; i++) {
        const range = formData.experienceRanges[i];
        if (!range.experienceMaxPerYear || !range.experienceFrom) {
          setMessage({ text: `Please fill all required fields for range ${i + 1}!`, type: 'error' });
          return;
        }
        if (range.experienceFrom && range.experienceTo &&
          parseInt(range.experienceFrom) > parseInt(range.experienceTo)) {
          setMessage({ text: `From experience must be less than To experience in range ${i + 1}!`, type: 'error' });
          return;
        }
      }
    }

    try {
      setIsSubmitting(true);
      const submitData = {
        leaveName: formData.leaveName,
        paidUnpaid: formData.paidUnpaid,
        maxPerYear: formData.byExperience ? 0 : parseInt(formData.maxPerYear),
        byExperience: formData.byExperience,
        experienceRanges: formData.byExperience ? formData.experienceRanges.map(range => ({
          experienceFrom: parseInt(range.experienceFrom) || 0,
          experienceTo: range.experienceTo && range.experienceTo !== '' ? parseInt(range.experienceTo) : null,
          experienceMaxPerYear: parseInt(range.experienceMaxPerYear)
        })) : []
      };

      if (editingLeaveType) {
        await api.leaveTypes.update(editingLeaveType.id, submitData);
        setMessage({ text: 'Leave type updated successfully!', type: 'success' });
      } else {
        await api.leaveTypes.create(submitData);
        setMessage({ text: 'Leave type created successfully!', type: 'success' });
      }
      await loadLeaveTypes();
      setTableRefreshKey((k) => k + 1);
      resetForm();
    } catch (error) {
      console.error('Error saving leave type:', error);
      setMessage({ text: 'Error saving leave type!', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    if (isSubmitting) return;
    setFormData({
      leaveName: '',
      paidUnpaid: 'Paid',
      maxPerYear: '',
      byExperience: false,
      experienceRanges: [{
        experienceFrom: '',
        experienceTo: '',
        experienceMaxPerYear: ''
      }]
    });
    setShowModal(false);
    setEditingLeaveType(null);
  };

  const handleEdit = async (leaveType) => {
    if (isActionLoading) return;
    setIsActionLoading(true);
    setActionLoadingText("Loading leave type details...");
    try {
      await new Promise((resolve) => setTimeout(resolve, 200));
      setEditingLeaveType(leaveType);

      // Handle experience ranges - use new format if available, fallback to old format
      let experienceRanges = [];
      if (leaveType.experienceRanges && leaveType.experienceRanges.length > 0) {
        experienceRanges = leaveType.experienceRanges.map(range => ({
          experienceFrom: range.experienceFrom ? range.experienceFrom.toString() : '',
          experienceTo: range.experienceTo !== null && range.experienceTo !== undefined ? range.experienceTo.toString() : '',
          experienceMaxPerYear: range.experienceMaxPerYear ? range.experienceMaxPerYear.toString() : ''
        }));
      } else if (leaveType.byExperience) {
        // Fallback to old single range format
        experienceRanges = [{
          experienceFrom: leaveType.experienceFrom ? leaveType.experienceFrom.toString() : '',
          experienceTo: leaveType.experienceTo !== null && leaveType.experienceTo !== undefined ? leaveType.experienceTo.toString() : '',
          experienceMaxPerYear: leaveType.experienceMaxPerYear ? leaveType.experienceMaxPerYear.toString() : ''
        }];
      } else {
        experienceRanges = [{
          experienceFrom: '',
          experienceTo: '',
          experienceMaxPerYear: ''
        }];
      }

      setFormData({
        leaveName: leaveType.leaveName,
        paidUnpaid: leaveType.paidUnpaid,
        maxPerYear: leaveType.byExperience ? '' : (leaveType.maxPerYear ? leaveType.maxPerYear.toString() : ''),
        byExperience: leaveType.byExperience || false,
        experienceRanges: experienceRanges
      });
      setShowModal(true);
    } catch (error) {
      console.error("Error opening edit leave type:", error);
    } finally {
      setIsActionLoading(false);
    }
  };
  const filteredLeaveTypes = (state.leaveTypes || []).filter((leaveType) => {

    const matchesLeaveName =
      !leaveTypeFilters.leaveName ||
      leaveType.leaveName
        .toLowerCase()
        .includes(leaveTypeFilters.leaveName.toLowerCase());

    const matchesType =
      !leaveTypeFilters.type ||
      leaveType.paidUnpaid === leaveTypeFilters.type;

    return matchesLeaveName && matchesType;
  });
  const handleInfo = (leaveType) => {
    setSelectedLeaveType(leaveType);
    setShowInfoModal(true);
  };

  const handleToggleStatus = async (leaveType) => {
    if (isActionLoading) return;
    const currentStatus = leaveType.status ?? 1;
    const newStatus = currentStatus === 1 ? 2 : 1;
    const isActivating = newStatus === 1;
    const actionText = isActivating ? "activate" : "inactivate";
    const leaveName = leaveType.leaveName || "this leave type";

    if (window.confirm(`Are you sure you want to ${actionText} ${leaveName}?`)) {
      setIsActionLoading(true);
      setActionLoadingText(`${isActivating ? "Activating" : "Inactivating"} leave type...`);
      try {
        await api.leaveTypes.toggleStatus(leaveType.id, newStatus);
        await loadLeaveTypes();
        setTableRefreshKey((k) => k + 1);
        setMessage({
          text: `Leave type ${isActivating ? "activated" : "inactivated"} successfully!`,
          type: "success",
        });
      } catch (error) {
        console.error("Error toggling leave type status:", error);
        setMessage({
          text: error.message || `Failed to ${actionText} leave type`,
          type: "error",
        });
      } finally {
        setIsActionLoading(false);
      }
    }
  };

  const handleDelete = async (id) => {
    if (isActionLoading) return;
    if (window.confirm('Are you sure?')) {
      setIsActionLoading(true);
      setActionLoadingText("Deleting leave type...");
      try {
        await api.leaveTypes.delete(id);
        await loadLeaveTypes();
        setTableRefreshKey((k) => k + 1);
        setMessage({ text: 'Leave type deleted successfully!', type: 'success' });
      } catch (error) {
        console.error('Error deleting leave type:', error);
        setMessage({ text: 'Error deleting leave type!', type: 'error' });
      } finally {
        setIsActionLoading(false);
      }
    }
  };

  return (
    <PermissionGuard module={MODULES.LEAVE_TYPES} action={ACTIONS.VIEW} submodule={MODULES.LEAVE_TYPES.submodules.LEAVE_TYPE_MASTER}>
      <div className="leave-type-master">
        {/* Full-screen blocking loader when action is triggered */}
        {isActionLoading && (
          <div
            className="full-screen-action-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              inset: 0,
              width: "100%",
              height: "100%",
              minWidth: "100%",
              minHeight: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
              zIndex: 999999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pointerEvents: "all",
              cursor: "wait",
            }}
          >
            <div
              className="full-screen-action-card"
              style={{
                background: "#ffffff",
                padding: "18px 28px",
                borderRadius: "8px",
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
                display: "flex",
                alignItems: "center",
                gap: "14px",
                pointerEvents: "auto",
              }}
            >
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: "2rem", height: "2rem" }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <span
                className="full-screen-action-text"
                style={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#212529",
                  letterSpacing: "0.2px",
                }}
              >
                {actionLoadingText || "Processing..."}
              </span>
            </div>
          </div>
        )}

        {message.text && (
          <Message
            message={message.text}
            type={message.type}
            onClose={() => setMessage({ text: '', type: '' })}
          />
        )}
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h2 className='hd_a'>Leave Type Master</h2>

          <div className="d-flex align-items-center gap-2">

            <button
              type="button"
              className={`btn ${Object.values(leaveTypeFilters).some(v => v !== "")
                  ? "btn-primary"
                  : "btn-outline-primary"
                }`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <i className="bi bi-funnel"></i>
            </button>

            <PermissionGuard
              module={MODULES.LEAVE_TYPES}
              action={ACTIONS.CREATE}
              submodule={MODULES.LEAVE_TYPES.submodules.LEAVE_TYPE_MASTER}
            >
              <button
                className="btn btn-primary"
                onClick={() => setShowModal(true)}
              >
                <i className="bi bi-plus-lg me-1"></i>Add Leave Type
              </button>
            </PermissionGuard>

          </div>
        </div>
        {/* <h2 className='hd_a'>Leave Type Master</h2>
        <PermissionGuard module={MODULES.LEAVE_TYPES} action={ACTIONS.CREATE} submodule={MODULES.LEAVE_TYPES.submodules.LEAVE_TYPE_MASTER}>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="bi bi-plus-lg me-1"></i>Add Leave Type
          </button>
        </PermissionGuard>
      </div> */}


        {showFilters && (
          <div className="card shadow-sm mb-3">
            <div className="card-body py-2">
              <div className="row g-3">

                <div className="col-md-4">
                  <label className="form-label">Leave Name</label>

                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search leave name"
                    value={leaveTypeFilters.leaveName}
                    onChange={(e) =>
                      setLeaveTypeFilters({
                        ...leaveTypeFilters,
                        leaveName: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Type</label>

                  <select
                    className="form-control"
                    value={leaveTypeFilters.type}
                    onChange={(e) =>
                      setLeaveTypeFilters({
                        ...leaveTypeFilters,
                        type: e.target.value,
                      })
                    }
                  >
                    <option value="">All</option>
                    <option value="Paid">Paid</option>
                    <option value="Unpaid">Unpaid</option>
                  </select>
                </div>

                <div className="col-md-2 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-outline-primary w-100"
                    onClick={() =>
                      setLeaveTypeFilters({
                        leaveName: "",
                        type: "",
                      })
                    }
                  >
                    Clear
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.3)', pointerEvents: isSubmitting ? 'none' : 'auto' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-sm border-0">
                <div className="modal-header border-bottom pt-2 pr-0 pb-2" style={{ backgroundColor: 'var(--primary-light)' }}>
                  <h6 className="modal-title" style={{ color: 'var(--primary-color)' }}>{editingLeaveType ? 'Edit' : 'Add'} Leave Type</h6>
                  <button type="button" className="btn-close" disabled={isSubmitting} onClick={resetForm}></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body p-3">
                    <div className="row g-2">
                      <div className="col-4">
                        <label className="form-label small text-muted">Leave Name *</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={formData.leaveName}
                          onChange={(e) => setFormData({ ...formData, leaveName: e.target.value })}
                          required
                        />
                      </div>
                      <div className="col-4">
                        <label className="form-label small text-muted">Type *</label>
                        <select
                          className="form-select form-select-sm"
                          value={formData.paidUnpaid}
                          onChange={(e) => setFormData({ ...formData, paidUnpaid: e.target.value })}
                          required
                        >
                          <option value="Paid">Paid</option>
                          <option value="Unpaid">Unpaid</option>
                        </select>
                      </div>
                      <div className="col-4">
                        <label className="form-label small text-muted">Max Per Year {!formData.byExperience && '*'}</label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={formData.maxPerYear}
                          onChange={(e) => setFormData({ ...formData, maxPerYear: e.target.value })}
                          placeholder="Enter number"
                          required={!formData.byExperience}
                          disabled={formData.byExperience}
                        />
                        {/* {!formData.byExperience && (
                        <button 
                          type="button" 
                          className="btn btn-sm btn-outline-success mt-1 w-100"
                          onClick={() => {
                            // Copy current form data and reset for new entry
                            const currentData = {
                              leaveName: formData.leaveName,
                              paidUnpaid: formData.paidUnpaid,
                              maxPerYear: formData.maxPerYear
                            };
                            // Reset form but keep copied values as reference
                            setFormData({
                              ...formData,
                              leaveName: formData.leaveName + ' (Copy)',
                              maxPerYear: formData.maxPerYear
                            });
                          }}
                          title="Copy current values to create similar leave type"
                        >
                          + Copy & Add New
                        </button>
                      )} */}
                      </div>
                      <div className="col-12">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="byExperience"
                            checked={formData.byExperience}
                            onChange={(e) => setFormData({ ...formData, byExperience: e.target.checked })}
                          />
                          <label className="form-check-label small text-muted" htmlFor="byExperience">
                            Experience-based Leave (Check worker's joining date)
                          </label>
                        </div>
                      </div>
                      {formData.byExperience && (
                        <>
                          <div className="col-12">
                            <div className="alert alert-info py-2 mb-2">
                              <small><strong>Experience-based Leave:</strong> Workers/Employee can only apply if their experience (from joining date) falls within the specified range.</small>
                            </div>
                          </div>
                          {formData.experienceRanges.map((range, index) => (
                            <React.Fragment key={index}>
                              <div className="col-4">
                                <label className="form-label small text-muted">Max Per Year *</label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={range.experienceMaxPerYear}
                                  onChange={(e) => {
                                    const newRanges = [...formData.experienceRanges];
                                    newRanges[index].experienceMaxPerYear = e.target.value;
                                    setFormData({ ...formData, experienceRanges: newRanges });
                                  }}
                                  placeholder="Enter number"
                                  required
                                />
                              </div>
                              <div className="col-4">
                                <label className="form-label small text-muted">From (Years) *</label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={range.experienceFrom === '' || range.experienceFrom == null ? 0 : range.experienceFrom}
                                  onChange={(e) => {
                                    const newRanges = [...formData.experienceRanges];
                                    newRanges[index].experienceFrom = e.target.value === '' ? 0 : Number(e.target.value);
                                    setFormData({ ...formData, experienceRanges: newRanges });
                                  }}
                                  min="0"
                                  placeholder="e.g. 1"
                                  required
                                />
                              </div>
                              <div className="col-4">
                                <label className="form-label small text-muted">To (Years)</label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={range.experienceTo}
                                  onChange={(e) => {
                                    const newRanges = [...formData.experienceRanges];
                                    newRanges[index].experienceTo = e.target.value;
                                    setFormData({ ...formData, experienceRanges: newRanges });
                                  }}
                                  min={range.experienceFrom || "0"}
                                  placeholder="Leave empty for 10+ years"
                                />
                                <small className="text-muted">Leave empty for unlimited (e.g., 10+ years)</small>
                                <div className="mt-1">
                                  {index === formData.experienceRanges.length - 1 && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary me-1"
                                      onClick={() => {
                                        const newRange = {
                                          experienceMaxPerYear: '',
                                          experienceFrom: range.experienceTo ? (parseInt(range.experienceTo) + 1).toString() : '',
                                          experienceTo: ''
                                        };
                                        setFormData({
                                          ...formData,
                                          experienceRanges: [...formData.experienceRanges, newRange]
                                        });
                                      }}
                                      title="Add new experience range"
                                    >
                                      + Add Range
                                    </button>
                                  )}
                                  {formData.experienceRanges.length > 1 && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => {
                                        const newRanges = formData.experienceRanges.filter((_, i) => i !== index);
                                        setFormData({ ...formData, experienceRanges: newRanges });
                                      }}
                                      title="Remove this range"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              </div>
                            </React.Fragment>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer bg-light py-2">
                    <button type="submit" className="btn btn-sm btn-primary" disabled={isSubmitting}>
                      {isSubmitting && (
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      )}
                      {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={isSubmitting} onClick={resetForm}>
                      Close
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {showInfoModal && selectedLeaveType && (
          <div
            className="modal"
            style={{
              display: "block",
              backgroundColor: "rgba(0,0,0,0.4)",
              zIndex: 1050,
            }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow border-0">
                <div
                  className="modal-header border-bottom"
                  style={{ backgroundColor: "var(--primary-light, #f8f9fa)" }}
                >
                  <h6 className="modal-title m-0 text-primary fw-bold">
                    <i className="bi bi-info-circle-fill me-2"></i>Leave Type Details
                  </h6>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowInfoModal(false);
                      setSelectedLeaveType(null);
                    }}
                  ></button>
                </div>
                <div className="modal-body p-3">
                  <table className="table table-bordered table-sm mb-0">
                    <tbody>
                      <tr>
                        <th style={{ width: "40%", background: "#f8f9fa" }}>
                          Leave Name
                        </th>
                        <td>{selectedLeaveType.leaveName}</td>
                      </tr>
                      <tr>
                        <th style={{ background: "#f8f9fa" }}>Status</th>
                        <td>
                          {(selectedLeaveType.status ?? 1) === 1 ? (
                            <span className="badge bg-success">Active</span>
                          ) : (
                            <span className="badge bg-danger">Inactive</span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th style={{ background: "#f8f9fa" }}>Type</th>
                        <td>
                          <span
                            className={`badge ${selectedLeaveType.paidUnpaid === "Paid" ? "bg-success" : "bg-warning"}`}
                          >
                            {selectedLeaveType.paidUnpaid}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <th style={{ background: "#f8f9fa" }}>Max Per Year</th>
                        <td>
                          {selectedLeaveType.byExperience ? (
                            selectedLeaveType.experienceRanges?.length ? (
                              selectedLeaveType.experienceRanges
                                .map((r) => r.experienceMaxPerYear)
                                .join(", ") + " days"
                            ) : (
                              (selectedLeaveType.experienceMaxPerYear || 0) + " days"
                            )
                          ) : (
                            `${selectedLeaveType.maxPerYear} days`
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th style={{ background: "#f8f9fa" }}>By Experience</th>
                        <td>{selectedLeaveType.byExperience ? "Yes" : "No"}</td>
                      </tr>
                      {selectedLeaveType.byExperience && (
                        <tr>
                          <th style={{ background: "#f8f9fa" }}>Experience Ranges</th>
                          <td>
                            {selectedLeaveType.experienceRanges?.length ? (
                              selectedLeaveType.experienceRanges.map((range, idx) => (
                                <div key={idx} className="small mb-1">
                                  <strong>{range.experienceFrom} - {range.experienceTo || "∞"} years:</strong> {range.experienceMaxPerYear} days
                                </div>
                              ))
                            ) : (
                              <span className="text-muted">None</span>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="modal-footer p-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setShowInfoModal(false);
                      setSelectedLeaveType(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-body px-0 pt-0">
            <CommonTable
              key={tableRefreshKey}
              dataSource={api.leaveTypes.table}
              queryParams={{ leaveName: leaveTypeFilters.leaveName, paidUnpaid: leaveTypeFilters.type }}
              columns={[
                { key: 'leaveName', label: 'Leave Name', sortable: true },
                { key: 'paidUnpaid', label: 'Type', sortable: true, render: (value, leaveType) => <span className={`badge ${value === 'Paid' ? 'bg-success' : 'bg-warning'}`}>{value}</span> },
                { key: 'maxPerYear', label: 'Max/Year', sortable: true, render: (value, leaveType) => leaveType.byExperience ? <div>{leaveType.experienceRanges?.length ? leaveType.experienceRanges.map(range => range.experienceMaxPerYear).join(', ') + ' days' : (leaveType.experienceMaxPerYear || 0) + ' days'}</div> : leaveType.maxPerYear },
                { key: 'experience', label: 'Experience', sortable: false, render: (value, leaveType) => leaveType.byExperience ? <div>{leaveType.experienceRanges?.length ? leaveType.experienceRanges.map((range, idx) => <span key={idx} className="badge bg-info me-1 mb-1 d-block">{range.experienceFrom}-{range.experienceTo || '∞'} years ({range.experienceMaxPerYear} days)</span>) : <span className="badge bg-info">{leaveType.experienceFrom}-{leaveType.experienceTo || '∞'} years</span>}</div> : <span className="text-muted">-</span> },
                {
                  key: 'status',
                  label: 'Status',
                  sortable: true,
                  render: (value, leaveType) => (
                    (leaveType.status ?? 1) === 1 ? (
                      <span className="badge bg-success" style={{ fontSize: '11px', padding: '4px 8px' }}>Active</span>
                    ) : (
                      <span className="badge bg-danger" style={{ fontSize: '11px', padding: '4px 8px' }}>Inactive</span>
                    )
                  )
                }
              ]}
              renderActions={(leaveType) => {
                const isActive = (leaveType.status ?? 1) === 1;
                return (
                  <div className="d-flex align-items-center gap-1">
                    <button
                      title="View Details"
                      className="btn btn-sm btn-primary"
                      disabled={isActionLoading}
                      onClick={() => handleInfo(leaveType)}
                      style={{ fontSize: '13px', padding: '4px 10px' }}
                    >
                      <i className="bi bi-info-circle-fill"></i>
                    </button>
                    {isActive && (
                      <PermissionGuard module={MODULES.LEAVE_TYPES} action={ACTIONS.EDIT} submodule={MODULES.LEAVE_TYPES.submodules.LEAVE_TYPE_MASTER}>
                        <button
                          title='Edit'
                          className="btn btn-sm btn-primary"
                          disabled={isActionLoading}
                          onClick={() => handleEdit(leaveType)}
                          style={{ fontSize: '13px', padding: '4px 10px' }}
                        >
                          <i className="bi bi-pencil-square"></i>
                        </button>
                      </PermissionGuard>
                    )}
                    {/* Hard delete commented out as per soft delete requirement
                    <PermissionGuard module={MODULES.LEAVE_TYPES} action={ACTIONS.DELETE} submodule={MODULES.LEAVE_TYPES.submodules.LEAVE_TYPE_MASTER}>
                      <button
                        title='Delete'
                        className="btn btn-sm btn-danger"
                        disabled={isActionLoading}
                        onClick={() => handleDelete(leaveType.id)}
                        style={{ fontSize: '13px', padding: '4px 10px' }}
                      >
                        <i className="bi bi-trash-fill"></i>
                      </button>
                    </PermissionGuard>
                    */}
                    <button
                      title={isActive ? "Inactivate Leave Type" : "Activate Leave Type"}
                      className={`btn btn-sm ${isActive ? "btn-outline-danger" : "btn-outline-success"}`}
                      onClick={() => handleToggleStatus(leaveType)}
                      disabled={isActionLoading}
                      style={{ fontSize: "13px", padding: "4px 10px" }}
                    >
                      {isActive ? (
                        <i className="bi bi-slash-circle" title="Inactivate"></i>
                      ) : (
                        <i className="bi bi-check-circle" title="Activate"></i>
                      )}
                    </button>
                  </div>
                );
              }}
            />
          </div>
        </div>
      </div>
    </PermissionGuard>
  );
}

export default LeaveTypeMaster;